import { loadRegisteredComponents, loadRegisteredComponentsOnPageLoad } from './componentLoader';
import { islandRegistry } from './islandRegistry';
import { UmbracoBridge } from '@common/umbraco';
import { initLegacyFontAwesomeIconUpgrade } from '@common/icons/iconSystem';
import {
  initPdfMediaLinkPopup,
  initPdfMediaLinkPopupOnPageLoad,
} from './components/standalones/files/PdfMediaLinkPopup';
import { isClickAnalyticsRuntimeEnabled } from './analytics/clickAnalyticsGate';
import type { ClickAnalyticsTracker } from './analytics/clickTypes';

declare global {
  interface Window {
    agoraClickAnalyticsTracker?: ClickAnalyticsTracker;
  }
}

const isBackoffice = document.body.id === 'umbracoMainPageBody';
const enablePdfMediaLinkPopup = document.body.dataset.enablePdfMediaLinkPopup === 'true';
const clickAnalyticsEndpoint =
  document.body.dataset.clickAnalyticsEndpoint || '/api/internal/analytics/clicks';
const clickAnalyticsEnabled = isClickAnalyticsRuntimeEnabled(
  document.body.dataset.clickAnalyticsEnabled,
  isBackoffice,
);

if (isBackoffice) {
  initPeriodically();
} else {
  initLegacyFontAwesomeIconUpgrade();
  initOnPageLoad();
  if (clickAnalyticsEnabled) {
    void startClickAnalytics();
  }
}

async function startClickAnalytics(): Promise<void> {
  if (window.agoraClickAnalyticsTracker) {
    window.agoraClickAnalyticsTracker.start();
    return;
  }

  const { createClickAnalyticsTracker } = await import('./analytics/createClickAnalyticsTracker');
  const tracker = createClickAnalyticsTracker({ endpoint: clickAnalyticsEndpoint });
  window.agoraClickAnalyticsTracker = tracker;
  tracker.start();
}

async function initPeriodically(maxRepetitions?: number) {
  const sleep = (delay) => new Promise((resolve) => setTimeout(resolve, delay));

  if (maxRepetitions) {
    for (let i = 0; i < maxRepetitions; ++i) {
      if (!UmbracoBridge()) {
        await sleep(500);
        --i;
        continue;
      }

      init();
      applyResizeFix();
      await sleep(1000);
    }
  } else {
    // noinspection InfiniteLoopJS
    while (true) {
      // @ts-ignore
      if (!window.UmbracoBridge) {
        await sleep(500);
        continue;
      }

      init();
      applyResizeFix();
      await sleep(1000);
    }
  }
}

function init() {
  void loadRegisteredComponents(islandRegistry);
  if (enablePdfMediaLinkPopup) {
    initPdfMediaLinkPopup();
  }
}

function initOnPageLoad() {
  loadRegisteredComponentsOnPageLoad(islandRegistry);
  if (enablePdfMediaLinkPopup) {
    initPdfMediaLinkPopupOnPageLoad();
  }
}

let elementDetectionTimes = new WeakMap();
let resizeFixStartTime = Date.now();

window.addEventListener('hashchange', function () {
  // Reset the start time and clear the detection times map
  resizeFixStartTime = Date.now();
  elementDetectionTimes = new WeakMap(); // Reinitialize the WeakMap
});

function applyResizeFix() {
  const currentTime = Date.now();
  const thresholdTime = resizeFixStartTime + 1000;

  function simulateMouseDown(element) {
    const event = new MouseEvent('mousedown', {
      bubbles: true,
      cancelable: true,
    });

    element.dispatchEvent(event);
  }

  const container = document.querySelector('umb-block-grid-root');
  const entries =
    container?.shadowRoot?.querySelectorAll(
      'umb-block-grid-entry > ng-form > .umb-block-grid__block',
    ) ?? [];

  entries.forEach((entry) => {
    if (!entry.hasAttribute('data-resize-fix')) {
      // Record the detection time if not already tracked
      if (!elementDetectionTimes.has(entry)) {
        elementDetectionTimes.set(entry, currentTime);
      }

      // Apply the fix only if the element is active and was created after 2 seconds
      const entryTime = elementDetectionTimes.get(entry);
      if (entry.classList.contains('--active') && entryTime >= thresholdTime) {
        const button = entry.querySelector('.umb-block-grid__scale-handler');
        if (button) {
          simulateMouseDown(button);
        }
      }

      // Mark the element to avoid future scans
      entry.setAttribute('data-resize-fix', 'true');
    }
  });
}
